import React, { useState, useRef, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Box } from '@mui/system';
import MenuItem from '@mui/material/MenuItem';
import dayjs from '../../config/dayjs';
import SelectButton from '../buttons/SelectButton';
import DatesFilter from './DatesFilter';
import useIsMobile from '../../hooks/useIsMobile';

const PREDEFINED_RANGES = [
  { label: 'Emitidas hoy', days: 0, mobileLabel: 'Emitidas hoy' },
  { label: 'Emitidas últimos 5 días', days: 5, mobileLabel: 'Últimos 5 días' },
  { label: 'Emitidas últimos 10 días', days: 10, mobileLabel: 'Últimos 10 días' },
  { label: 'Emitidas últimos 15 días', days: 15, mobileLabel: 'Últimos 15 días' },
  { label: 'Emitidas últimos 30 días', days: 30, mobileLabel: 'Últimos 30 días' },
];

const isRangeCurrentlySet = (dates) => dates.dateIssued_Gte && dates.dateIssued_Lte;

const DatesSelectFilter = ({
  setStates,
  setDates,
  states,
  dates,
  defaultDaysValue,
}) => {
  const [datesAnchorEl, setDatesAnchorEl] = useState(null);
  const selectButtonRef = useRef(null);
  const isMobile = useIsMobile('tablet');

  const currentValue = useMemo(() => {
    if (!dates.dateIssued_Gte || !dates.dateIssued_Lte) {
      return defaultDaysValue;
    }

    const startDate = dayjs(dates.dateIssued_Gte);
    const endDate = dayjs(dates.dateIssued_Lte);
    const daysDiff = endDate.diff(startDate, 'day');
    const matchingRange = PREDEFINED_RANGES.find((range) => range.days === daysDiff);

    return matchingRange ? matchingRange.days : 'custom';
  }, [dates.dateIssued_Gte, dates.dateIssued_Lte, defaultDaysValue]);

  const handlePredefinedRange = (days) => {
    const endDate = dayjs();
    const startDate = dayjs().subtract(days, 'days');

    setDates({
      dateIssued_Gte: startDate,
      dateIssued_Lte: endDate,
    });
    setStates({
      ...states,
      dateIssued_Gte: startDate.format('YYYY-MM-DD'),
      dateIssued_Lte: endDate.format('YYYY-MM-DD'),
    });
  };

  useEffect(() => {
    if (!isRangeCurrentlySet(dates) && defaultDaysValue) {
      handlePredefinedRange(defaultDaysValue);
    }
  }, [defaultDaysValue]);

  const openDateFilter = ({ currentTarget }) => setDatesAnchorEl(currentTarget);
  const closeDateFilter = () => setDatesAnchorEl(null);

  return (
    <Box>
      <SelectButton
        id="filter-dates"
        value={currentValue}
        buttonRef={selectButtonRef}
      >
        {PREDEFINED_RANGES.map((range) => (
          <MenuItem
            key={range.days}
            value={range.days}
            onClick={() => handlePredefinedRange(range.days)}
          >
            {isMobile ? range.mobileLabel : range.label}
          </MenuItem>
        ))}
        <MenuItem
          value="custom"
          onClick={() => openDateFilter({ currentTarget: selectButtonRef.current })}
        >
          Intervalo personalizado
        </MenuItem>
      </SelectButton>
      <DatesFilter
        anchorEl={datesAnchorEl}
        close={closeDateFilter}
        dates={dates}
        setDates={setDates}
        states={states}
        setStates={setStates}
      />
    </Box>
  );
};

DatesSelectFilter.propTypes = {
  setStates: PropTypes.func,
  setDates: PropTypes.func,
  states: PropTypes.shape({
    dateIssued_Gte: PropTypes.string,
    dateIssued_Lte: PropTypes.string,
  }),
  defaultDaysValue: PropTypes.number,
  dates: PropTypes.shape({
    dateIssued_Gte: PropTypes.instanceOf(dayjs),
    dateIssued_Lte: PropTypes.instanceOf(dayjs),
  }),
};

DatesSelectFilter.defaultProps = {
  setStates: () => {},
  setDates: () => {},
  states: {},
  dates: {},
  defaultDaysValue: 30,
};

export default DatesSelectFilter;
